import Vue from 'vue'
import { useRouter } from '@core/utils/utils'
import { ref } from '@vue/composition-api'
import api from '../services/api'

// Rotulo, cor e id do filtro rapido de cada situacao de declaracao.
// A CHAVE e o valor numerico de ESituacaoDeclaracao (back) e o "filtroId" precisa bater
// exatamente com o "id" de filtrosRapidos em DeclaracaoDctfWebView.vue — e por ele que o
// deep-link ?filtroRapido= encontra o filtro. Antes o grafico mandava o NOME da serie, e
// como nao existia entrada "Outros" na grid o filtro era descartado em silencio.
const SITUACOES = {
  1: { nome: 'Em Andamento', filtroId: 'Em Andamento', cor: 'rgb(49, 94, 158)' },
  2: { nome: 'Ativa', filtroId: 'Ativa', cor: 'rgb(77, 159, 114)' },
  3: { nome: 'Retificada', filtroId: 'Retificada', cor: 'rgb(247, 210, 74)' },
  4: { nome: 'Excluída', filtroId: 'Excluida', cor: 'rgb(130, 134, 139)' },
  5: { nome: 'Indevida', filtroId: 'Indevida', cor: 'rgb(158, 105, 175)' },
  6: { nome: 'Sem transmissão', filtroId: 'Sem transmissão', cor: 'rgb(234, 84, 85)' },
}

// Ordem do esqueleto, so para a carga inicial — a ordem real das series vem do backend.
const ORDEM_PADRAO = [2, 1, 3, 6, 4, 5]

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ']

// Monta o trecho de query do periodo a partir do rotulo do eixo X.
// Devolve null quando o rotulo nao e reconhecido, para nao navegar com filtro vazio.
function queryDoPeriodo(eixoX, ano) {
  const mes = MESES.indexOf(eixoX)
  if (mes >= 0) { return { filtroMes: `${ano}-${String(mes + 1).padStart(2, '0')}` } }
  if (eixoX === 'ANUAL') { return { filtroAno: `${ano}` } }
  return null
}

export default function (anoInicial, filtros, rota) {
  const router = rota == null ? useRouter().router : rota
  const ano = ref(anoInicial)

  // Metadado de cada serie na MESMA ordem em que elas entram no grafico.
  // O clique resolve o filtro por aqui, e nao por uma propriedade extra no objeto da serie:
  // o ApexCharts nao garante preservar campos que ele nao conhece.
  let situacoesPorIndice = []

  // Esqueleto com as 6 series zeradas ate a API responder: comecar com [] deixa o cartao
  // sem legenda e faz a tela saltar quando os dados chegam (mesmo sintoma ja registrado
  // para o placeholder do useCaixaPostal).
  // Zeros explicitos, e nao "data: []": com todas as series vazias o ApexCharts nao desenha
  // a legenda (medido), entao o esqueleto nao evitaria o salto — que e justamente o motivo
  // de ele existir. Com os pontos em zero a legenda e o eixo ja aparecem na carga.
  const seriesVazias = ORDEM_PADRAO.map(situacao => ({
    name: SITUACOES[situacao].nome,
    color: SITUACOES[situacao].cor,
    data: [...MESES, 'ANUAL'].map(mes => ({ x: mes, y: 0 })),
  }))

  const dados = ref({
    // "total" precisa nascer aqui: propriedade acrescentada depois a um objeto ja observado
    // nao e reativa no Vue 2, e o cartao mostraria " Declaracoes" sem numero.
    total: 0,
    series: seriesVazias,
    chartOptions: {
      chart: {
        stacked: true,
        events: {
          // clique numa fatia da barra: leva o periodo + a situacao daquela fatia
          dataPointSelection(event, chartContext, grafico) {
            const eixoX = grafico.w.config.series[grafico.seriesIndex].data[grafico.dataPointIndex].x
            const periodo = queryDoPeriodo(eixoX, ano.value)
            const situacao = situacoesPorIndice[grafico.seriesIndex]
            if (periodo === null || situacao === undefined) { return }

            localStorage.setItem('filtrosDashboard', JSON.stringify(filtros))
            router.push({ path: 'declaracao-dctf-web/visualizar', query: { ...periodo, filtroRapido: situacao.filtroId } })
          },
          // clique no rotulo do mes (base do grafico): leva so o periodo, sem situacao
          click(event) {
            // "toElement" e nao-padrao (so Chromium): no Firefox e undefined e ler .innerHTML
            // lanca TypeError. "target" cobre os dois.
            const alvo = event.target || event.toElement
            const periodo = queryDoPeriodo(alvo && alvo.innerHTML, ano.value)
            if (periodo === null) { return }

            localStorage.setItem('filtrosDashboard', JSON.stringify(filtros))
            router.push({ path: 'declaracao-dctf-web/visualizar', query: periodo })
          },
        },
      },
      xaxis: {
        categories: [...MESES, 'ANUAL'],
      },
      plotOptions: {
        bar: {
          borderRadius: 5,
        },
      },
    },
  })

  api.getContadoresDctfWeb(ano.value, filtros)
    .then(payload => {
      // Uma lista so: as series do grafico e o metadado por indice PRECISAM ficar alinhados,
      // entao os dois derivam do mesmo array filtrado, nunca de duas expressoes paralelas.
      // Situacao desconhecida (enum novo no back, front antigo) e descartada em vez de
      // virar uma barra sem rotulo nem cor.
      const series = (payload.data.series || []).filter(serie => SITUACOES[serie.situacao] !== undefined)

      situacoesPorIndice = series.map(serie => SITUACOES[serie.situacao])

      // A cor vai junto de cada serie aqui, e nao so na criacao do grafico: este array
      // SUBSTITUI o anterior, entao cor definida apenas ali seria descartada na carga.
      dados.value.series = series.map(serie => ({
        name: SITUACOES[serie.situacao].nome,
        color: SITUACOES[serie.situacao].cor,
        data: serie.colunas,
      }))

      dados.value.total = payload.data.total
    })
    .catch(() => {
      // Sem tratamento, a promessa rejeita em silencio e o grafico fica zerado — indistinguivel
      // de "cliente sem declaracoes". Avisa pelo plugin do projeto (nunca console.*, que quebra
      // o build de producao) e zera o total.
      // Nao reatribuir "series" aqui: seria a MESMA referencia do esqueleto, o setter do Vue
      // faria early-return e nem o total seria renderizado.
      dados.value.total = 0
      Vue.prototype.$erro('Não foi possível carregar o gráfico de DCTFWeb.')
    })

  return {
    dadosDctfWebStatus: dados,
    anoDash: ano,
    routerDctf: router,
  }
}
